/**
 * API 클라이언트. 개발 모드에서는 테넌트·사용자를 헤더로 넘긴다 (AUTH_MODE=dev).
 * OIDC 모드에서는 Authorization: Bearer 를 붙인다 (로그인 플로우는 후속 작업).
 */
import { setSessionExpiry } from './session.ts';
import { tenantSlug } from './tenant.ts';
import { supportMode } from './support.ts';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

export { tenantSlug } from './tenant.ts';

function baseHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'X-Requested-With': 'PRISM' }; // CSRF: 쿠키 세션의 상태 변경 요청에 필수
  // CR-019 지원 열람 모드: 대상 테넌트 슬러그 + 세션 헤더 (플랫폼 관리 API 는 제외)
  const sup = supportMode();
  const slug = tenantSlug();
  if (sup) { h['X-Tenant-Slug'] = sup.tenantSlug; h['X-Support-Session'] = sup.sessionId; }
  else if (slug) h['X-Tenant-Slug'] = slug;
  // 개발 모드 헤더 로그인은 쿠키 세션이 없을 때만 백엔드가 사용한다. 명시적으로 켠 경우에만 보낸다.
  let devEmail: string | null = null;
  try { devEmail = localStorage.getItem('prism.devUserEmail'); } catch { /* ignore */ }
  if (devEmail) h['X-Dev-User-Email'] = devEmail;
  return h;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...baseHeaders(), ...(init.headers as Record<string, string> | undefined) };
  // 플랫폼 관리 API 는 지원 모드 중에도 플랫폼 테넌트로 호출한다
  if (path.startsWith('/platform/') && headers['X-Support-Session']) { delete headers['X-Support-Session']; headers['X-Tenant-Slug'] = tenantSlug() ?? headers['X-Tenant-Slug']; }
  const res = await fetch(`${BASE}${path}`, { ...init, credentials: 'same-origin', headers });
  const exp = res.headers.get('X-Session-Expires-At');
  if (exp) setSessionExpiry(exp, res.headers.get('X-Session-Absolute-Expires-At'));
  if (res.status === 401) setSessionExpiry(null, null);
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { /* ignore */ }
    const msg = (body as { message?: string | string[] })?.message;
    throw new ApiError(res.status, Array.isArray(msg) ? msg.join(', ') : (msg ?? res.statusText), body);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export interface Me {
  id: string; tenantId: string; tenantSlug: string; tenantName: string; email: string; name: string; role: string; deptId: string | null;
  breakGlass: boolean; sessionId?: string; authMethod: string; mfaVerified: boolean; mfaPending: boolean; mfaEnrolled: boolean; mfaMethods?: Array<'totp' | 'email'>; mfaAllowed?: Array<'totp' | 'email'>; mustChangePassword: boolean;
  support?: { sessionId: string; reason: string; expiresAt: string; platformTenantSlug: string };
  permissions: Record<string, 'tenant' | 'dept_tree' | 'dept' | 'own'>; deptIds: string[]; roleIds: string[];
}
export type LoginOutcome = { status: 'ok' | 'mfa_required' | 'mfa_enroll_required' };
/** 텍스트 응답(CSV 등)을 같은 인증 헤더로 받는다 (CR-056) */
export async function apiText(path: string): Promise<{ text: string; filename: string }> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'same-origin', headers: baseHeaders() });
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  const cd = res.headers.get('Content-Disposition') ?? ''; const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  return { text: await res.text(), filename: m ? decodeURIComponent(m[1]) : 'download.csv' };
}
export const post = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
export const put = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body: JSON.stringify(body) });
export const patch = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
export const del = <T,>(path: string) => api<T>(path, { method: 'DELETE' });
export interface Framework { id: string; tenantId: string | null; code: string; name: string; version: string; effectiveFrom: string | null; sourceUrl: string | null; _count: { controls: number } }
export interface Control { id: string; tenantId: string | null; frameworkId: string; code: string; title: string; groupCode: string | null; groupTitle: string | null; orderNo: number; checkCycle: string; policyReviewCycle: string; requiredEvidenceTypes: string[]; ownerDeptId: string | null }
export interface ControlDetail extends Control { requirementText: string; framework: { id: string; code: string; name: string; version: string }; mappings: Array<{ relation: string; control: { id: string; code: string; title: string; frameworkId: string } }> }
